import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Envelope,
  PaperPlaneTilt,
  CircleNotch,
  Wrench,
  ChartLineDown,
  Files,
  CheckCircle,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function LeoDigestPanel() {
  const { user, updateProfile } = useAuth();
  const [status, setStatus] = useState(null); // { enabled, last_sent, email }
  // Prefer the freshly-loaded /status response; fall back to AuthContext
  // (which rehydrates from /auth/me) until status arrives.
  const enabled =
    status?.enabled !== undefined ? status.enabled : user?.leo_digest_enabled !== false;
  const [preview, setPreview] = useState(null); // { signals, narrative }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/leo/digest/status");
      setStatus(r.data);
    } catch (e) {
      // silent — keep UI usable
      console.error("Leo digest status load failed", e);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const onTogglePreview = async () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    setLoadingPreview(true);
    try {
      const r = await api.get("/leo/digest/preview", { timeout: 90000 });
      setPreview(r.data);
      setShowPreview(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Leo couldn't compose a preview right now.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const onSendNow = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await api.post("/leo/digest/send-now", {}, { timeout: 90000 });
      if (r.data?.sent) {
        toast.success("Leo&apos;s Notes sent to your inbox.");
      } else if (r.data?.email?.dev_log) {
        toast.success("Digest composed — email logged server-side (dev mode).");
      } else {
        toast.error(r.data?.email?.error || "Couldn't send — try again in a moment.");
      }
      await loadStatus();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const onToggleEnabled = async (next) => {
    if (toggling) return;
    setToggling(true);
    const res = await updateProfile({ leo_digest_enabled: next });
    if (!res.ok) {
      toast.error(res.error || "Couldn't save preference.");
    } else {
      toast.success(next ? "Weekly digest enabled." : "Weekly digest paused.");
      await loadStatus();
    }
    setToggling(false);
  };

  const totals = preview?.signals?.totals;

  return (
    <section
      data-testid="leo-digest-panel"
      className="border border-[#E0DDD6] bg-white p-5"
    >
      <header className="flex items-start gap-3">
        <div className="w-9 h-9 bg-[#1A3622] flex items-center justify-center border border-[#0F2818] shrink-0">
          <Envelope size={16} weight="regular" color="#F9F8F6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
            Leo&apos;s Notes · Weekly Digest
          </div>
          <h3 className="font-serif-display text-lg text-[#0A192F] leading-tight mt-0.5">
            A short stewardship review, in your inbox.
          </h3>
          <p className="text-xs text-[#5C5F58] mt-1">
            Every Monday, Leo scans your ledger and emails one short note —
            overdue maintenance, valuations that need a refresh, and high-value
            items still missing documents.
          </p>
        </div>
      </header>

      {/* Toggle row */}
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#E0DDD6] pt-4">
        <div className="min-w-0">
          <div className="text-sm text-[#0A192F]">
            {enabled ? (
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-[#1A3622]" />
                Weekly digest is on
              </span>
            ) : (
              <span className="text-[#5C5F58]">Weekly digest is paused</span>
            )}
          </div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mt-1">
            {status?.last_sent
              ? `Last sent ${fmtDate(status.last_sent)}`
              : "No digest sent yet"}
            {status?.email ? ` · ${status.email}` : ""}
          </div>
        </div>
        <button
          onClick={() => onToggleEnabled(!enabled)}
          disabled={toggling}
          data-testid="leo-digest-toggle"
          className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
            enabled
              ? "border-[#5C5F58] text-[#5C5F58] hover:border-[#6B1718] hover:text-[#6B1718]"
              : "border-[#1A3622] text-[#1A3622] hover:bg-[#1A3622] hover:text-[#F9F8F6]"
          } disabled:opacity-50`}
        >
          {toggling ? "Saving…" : enabled ? "Pause" : "Turn on"}
        </button>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={onTogglePreview}
          disabled={loadingPreview}
          data-testid="leo-digest-preview-btn"
          className="inline-flex items-center gap-2 border border-[#1A3622] text-[#1A3622] px-3 py-1.5 text-xs hover:bg-[#1A3622] hover:text-[#F9F8F6] disabled:opacity-50 transition-colors"
        >
          {loadingPreview ? (
            <CircleNotch size={12} className="animate-spin" />
          ) : null}
          {showPreview ? "Hide preview" : loadingPreview ? "Composing…" : "Preview this week's digest"}
        </button>
        <button
          onClick={onSendNow}
          disabled={sending}
          data-testid="leo-digest-send-now"
          className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-3 py-1.5 text-xs border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
        >
          {sending ? (
            <CircleNotch size={12} className="animate-spin" />
          ) : (
            <PaperPlaneTilt size={12} weight="bold" />
          )}
          {sending ? "Sending…" : "Send to my inbox now"}
        </button>
      </div>

      {/* Preview body */}
      {showPreview && preview && (
        <div className="mt-4 border-t border-[#E0DDD6] pt-4 space-y-4" data-testid="leo-digest-preview">
          <div className="bg-[#F9F8F6] border-l-2 border-[#1A3622] px-4 py-3">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
              Subject
            </div>
            <div className="font-serif-display text-base text-[#0A192F]">
              {preview.narrative.subject}
            </div>
          </div>
          <p className="text-sm text-[#0A192F] leading-relaxed italic">
            {preview.narrative.intro}
          </p>

          {/* Signal counters */}
          <div className="grid grid-cols-3 gap-2" data-testid="leo-digest-counters">
            <Counter
              icon={Wrench}
              label="Overdue"
              count={totals?.overdue || 0}
              tint="bg-[#6B1718]"
            />
            <Counter
              icon={ChartLineDown}
              label="Valuation drift"
              count={totals?.drift || 0}
              tint="bg-[#9C5012]"
            />
            <Counter
              icon={Files}
              label="Document gaps"
              count={totals?.gaps || 0}
              tint="bg-[#1A3622]"
            />
          </div>

          {/* Per-section detail */}
          <SignalList
            title="Overdue maintenance"
            items={preview.signals.overdue_maintenance}
            renderItem={(r) => (
              <>
                <span className="text-sm text-[#0A192F]">{r.title}</span>
                <span className="font-mono-ledger text-[10px] uppercase tracking-[0.15em] text-[#6B1718]">
                  {r.days_overdue}d overdue · {r.due_date}
                </span>
              </>
            )}
          />
          <SignalList
            title="Valuation drift candidates"
            items={preview.signals.valuation_drift}
            renderItem={(r) => (
              <>
                <span className="text-sm text-[#0A192F]">{r.name}</span>
                <span className="font-mono-ledger text-[10px] uppercase tracking-[0.15em] text-[#5C5F58]">
                  {r.fmv ? fmtMoney(r.fmv) : "no FMV"} · {r.reason}
                </span>
              </>
            )}
          />
          <SignalList
            title="Document gaps"
            items={preview.signals.document_gaps}
            renderItem={(r) => (
              <>
                <span className="text-sm text-[#0A192F]">{r.name}</span>
                <span className="font-mono-ledger text-[10px] uppercase tracking-[0.15em] text-[#5C5F58]">
                  {fmtMoney(r.fmv)} · {r.category}
                </span>
              </>
            )}
          />

          {totals && totals.overdue + totals.drift + totals.gaps === 0 && (
            <div className="text-center py-6 bg-[#F9F8F6] border border-[#E0DDD6]">
              <div className="font-serif-display text-base text-[#1A3622]">
                The ledger is steady this week.
              </div>
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mt-1">
                Nothing flagged
              </div>
            </div>
          )}

          <p className="text-xs text-[#5C5F58] italic">
            {preview.narrative.closing}
          </p>
        </div>
      )}
    </section>
  );
}

function Counter({ icon: Icon, label, count, tint }) {
  return (
    <div className="border border-[#E0DDD6] bg-white p-3 text-center">
      <div className={`w-7 h-7 ${tint} flex items-center justify-center mx-auto mb-1.5`}>
        <Icon size={14} weight="regular" color="#F9F8F6" />
      </div>
      <div className="font-serif-display text-2xl text-[#0A192F] leading-none">{count}</div>
      <div className="font-mono-ledger text-[9px] uppercase tracking-[0.2em] text-[#5C5F58] mt-1">
        {label}
      </div>
    </div>
  );
}

function SignalList({ title, items, renderItem }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        {title} · {items.length}
      </div>
      <ul className="space-y-1">
        {items.map((r, i) => (
          <li
            key={i}
            className="border border-[#E0DDD6] bg-white px-3 py-2 flex items-center justify-between gap-3"
          >
            {renderItem(r)}
          </li>
        ))}
      </ul>
    </div>
  );
}
